import { UploadCloud, FileText, Layers, Lightbulb, FileCheck2 } from 'lucide-react';

import Container from '../../Main/sections/Container';
import Reveal from '../../../components/Reveal';
import { useScrollProgress } from '../../../hooks/useScrollProgress';

const STEPS = [
  {
    n: '1',
    title: 'Upload Your Notes',
    body: 'Drag and drop any file or paste a link. PDFs, slides, videos, Word docs, and plain text — no reformatting needed.',
  },
  {
    n: '2',
    title: 'AI reads and structures it',
    body: 'Our AI analyses your content, identifies key concepts, highlights critical information, and organises everything into clean sections.',
  },
  {
    n: '3',
    title: 'Your full study set is ready',
    body: 'Notes, flashcards, quiz questions, and a smart summary — all generated and ready to use in seconds.',
  },
];

const N = STEPS.length;

/* ---------- right-side visuals ---------- */

const UploadVisual = () => (
  <div className="card-dark rounded-2xl p-6">
    <div className="mb-4 flex items-center gap-2 text-white">
      <UploadCloud size={18} className="text-accent" />
      <span className="text-sm font-semibold">Upload Your Material</span>
    </div>
    <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-white/15 bg-white/[0.02] py-12 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent/12 text-accent">
        <UploadCloud size={22} />
      </span>
      <p className="text-sm font-medium text-white">Drop your file here or paste a link</p>
      <p className="max-w-xs text-[0.75rem] text-white/40">
        Supports PDF, PPTX, DOCX, MP4, YouTube URLs and plain text
      </p>
    </div>
  </div>
);

const AnalyzeVisual = () => (
  <div className="card-dark rounded-2xl p-6">
    <div className="mb-5 flex items-center gap-2">
      <span className="h-2 w-2 animate-pulse rounded-full bg-accent" />
      <span className="text-sm font-semibold text-white">AI is analysing your content…</span>
    </div>
    <div className="rounded-xl border border-white/8 bg-white/[0.03] p-4">
      <div className="mb-2 flex items-center justify-between text-[0.8rem]">
        <span className="flex items-center gap-2 text-white/80">
          <FileText size={14} className="text-accent" />
          Reading Biology_Chapter4_Mitosis.pdf
        </span>
        <span className="font-semibold text-accent">72%</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
        <div className="h-full w-[72%] rounded-full bg-gradient-to-r from-accent to-accentDark" />
      </div>
    </div>
    <div className="mt-3 flex flex-col gap-2">
      {['Detecting key concepts', 'Structuring sections', 'Highlighting definitions'].map((t) => (
        <div key={t} className="flex items-center gap-2 text-[0.78rem] text-white/45">
          <span className="h-1.5 w-1.5 rounded-full bg-accent/60" />
          {t}
        </div>
      ))}
    </div>
  </div>
);

const ReadyVisual = () => {
  const items = [
    { icon: FileText, title: 'Interactive Notes', meta: '5 sections · 24 highlights' },
    { icon: Layers, title: 'Flashcard Set', meta: '18 cards generated' },
    { icon: Lightbulb, title: 'Practice Quiz', meta: '10 questions' },
    { icon: FileCheck2, title: 'Smart Summary', meta: '1-page overview' },
  ];
  return (
    <div className="card-dark rounded-2xl p-6">
      <div className="mb-4 flex items-center gap-2 text-white">
        <FileCheck2 size={18} className="text-accent" />
        <span className="text-sm font-semibold">Your study set is ready</span>
      </div>
      <div className="flex flex-col gap-2.5">
        {items.map((it) => (
          <div
            key={it.title}
            className="flex items-center gap-3 rounded-xl border border-white/8 bg-white/[0.03] px-4 py-3"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent/12 text-accent">
              <it.icon size={15} />
            </span>
            <span className="text-[0.85rem] font-medium text-white">{it.title}</span>
            <span className="ml-auto text-[0.72rem] text-white/45">{it.meta}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const VISUALS = [UploadVisual, AnalyzeVisual, ReadyVisual];

const Header = () => (
  <div className="text-center">
    <span className="eyebrow">How It Works</span>
    <h2 className="font-heading mx-auto mt-5 max-w-3xl text-[2rem] font-bold leading-[1.1] sm:text-[2.5rem] lg:text-[2.9rem]">
      <span className="head-fade">From Raw Material To </span>
      <span className="text-accent-grad">Ready-To-Study Notes</span>
    </h2>
    <p className="mx-auto mt-4 max-w-xl text-[0.95rem] text-white/55">
      Three steps. Under 30 seconds. Your entire study set, done.
    </p>
  </div>
);

const StepRow = ({ step, isActive, fill, onClick }) => (
  <div className="flex gap-4" style={{ opacity: isActive ? 1 : 0.45, transition: 'opacity 0.4s ease' }}>
    <div className="relative mt-1 w-[3px] shrink-0 rounded-full bg-white/10">
      <span
        className="absolute left-0 top-0 w-full rounded-full bg-accent"
        style={{ height: `${fill * 100}%`, transition: 'height 0.1s linear' }}
      />
    </div>
    <button onClick={onClick} className="text-left">
      <div className="flex items-center gap-3">
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-[0.75rem] font-semibold transition-colors ${
            isActive ? 'border-accent bg-accent/10 text-accent' : 'border-white/20 text-white/50'
          }`}
        >
          {step.n}
        </span>
        <span
          className={`font-heading text-[1.1rem] font-semibold transition-colors md:text-[1.25rem] ${
            isActive ? 'text-accent' : 'text-white'
          }`}
        >
          {step.title}
        </span>
      </div>
      <div className="grid transition-all duration-500" style={{ gridTemplateRows: isActive ? '1fr' : '0fr' }}>
        <p className="overflow-hidden pl-[2.5rem] pt-2 text-[0.9rem] leading-relaxed text-white/60">
          {step.body}
        </p>
      </div>
    </button>
  </div>
);

const NotesHowItWorks = () => {
  const { ref, progress } = useScrollProgress();
  const scaled = progress * N;
  const index = Math.min(N - 1, Math.floor(scaled));

  return (
    <section ref={ref} className="relative lg:h-[360vh] pb-36">
      {/* desktop pinned */}
      <div className="sticky top-0 hidden h-screen items-center overflow-hidden lg:flex">
        <div className="glow left-1/2 top-0 h-[420px] w-[700px] -translate-x-1/2 bg-accent/8" />
        <Container className="relative w-full">
          <Header />
          <div className="mt-12 grid grid-cols-[1fr_1fr] items-center gap-14">
            <div className="flex flex-col gap-8">
              {STEPS.map((s, i) => {
                const fill = i < index ? 1 : i === index ? Math.min(1, Math.max(0, scaled - i)) : 0;
                return (
                  <StepRow
                    key={s.n}
                    step={s}
                    isActive={i === index}
                    fill={fill}
                    onClick={() => {
                      const el = ref.current;
                      if (!el) return;
                      const distance = el.offsetHeight - window.innerHeight;
                      window.scrollTo({
                        top: el.offsetTop + ((i + 0.5) / N) * distance,
                        behavior: 'smooth',
                      });
                    }}
                  />
                );
              })}
            </div>
            <div className="relative h-[300px]">
              {VISUALS.map((V, i) => (
                <div
                  key={i}
                  className="absolute inset-0 transition-all duration-500 ease-out"
                  style={{
                    opacity: i === index ? 1 : 0,
                    transform: i === index ? 'translateY(0) scale(1)' : 'translateY(14px) scale(0.98)',
                    pointerEvents: i === index ? 'auto' : 'none',
                  }}
                >
                  <V />
                </div>
              ))}
            </div>
          </div>
        </Container>
      </div>

      {/* mobile stacked */}
      <div className="lg:hidden">
        <Container className="py-16">
          <Reveal>
            <Header />
          </Reveal>
          <div className="mt-10 flex flex-col gap-10">
            {STEPS.map((s, i) => {
              const V = VISUALS[i];
              return (
                <Reveal key={s.n} delay={40}>
                  <div className="flex items-center gap-3">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full border border-accent bg-accent/10 text-[0.8rem] font-semibold text-accent">
                      {s.n}
                    </span>
                    <span className="font-heading text-[1.2rem] font-semibold text-accent">
                      {s.title}
                    </span>
                  </div>
                  <p className="mt-2 pl-11 text-[0.9rem] leading-relaxed text-white/60">{s.body}</p>
                  <div className="mt-5">
                    <V />
                  </div>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </div>
    </section>
  );
};

export default NotesHowItWorks;
